import React, { useCallback, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  getUserManagementState,
  clearAllFilters,
  UserManagementState,
  UserManagementFilterConstants,
} from '../../store/userManagement/userManagementSlice'
import FilterChips, { FilterChipData } from '../FilterChips/FilterChips'

// Interface for User Management filter types - defines available filter categories
interface UserManagementFilters {
  Tools: string
  Status: string
}

/**
 * Helper function to convert User Management filter state into FilterChipData format
 * for display in the FilterChips component. Processes both tool and status filters.
 *
 * @param filters - Current User Management state containing active filters
 * @returns Promise<FilterChipData<UserManagementFilters>[]> - Array of chip data for rendering
 */
const getChipsData = async (
  filters: UserManagementState,
): Promise<FilterChipData<UserManagementFilters>[]> => {
  const chipsData: FilterChipData<UserManagementFilters>[] = []

  // Add tool filter chips
  if (filters.toolFilters?.length > 0) {
    const toolChips = filters.toolFilters.map((tool: string) => ({
      property:
        UserManagementFilterConstants.tools as keyof UserManagementFilters,
      value: tool,
    }))
    chipsData.push(...toolChips)
  }

  // Add status filter chips
  if (filters.statusFilters?.length > 0) {
    const statusChips = filters.statusFilters.map((status: string) => ({
      property:
        UserManagementFilterConstants.status as keyof UserManagementFilters,
      value: status,
    }))
    chipsData.push(...statusChips)
  }

  return chipsData
}

/**
 * Props interface for UserManagementChips component
 * Following the established pattern from InvoicesChips
 */
interface UserManagementChipsProps {
  /** Handler function to trigger filter changes and search operations */
  changeFilterAndSearch?: (filterOption: string, value: string) => void
  /** Handler function to clear all active filters */
  clearAllFilters?: () => void
}

/**
 * UserManagementChips Component
 *
 * Displays active filter selections as removable chips in the User Management interface.
 * Visualizes active tool and status filters with individual deletion and clear-all functionality.
 * Follows the established Invoicing pattern for consistent filter chip behavior.
 *
 * Features:
 * - Displays tool and status filters as removable chips
 * - Individual chip deletion with toggle functionality
 * - Clear all filters functionality with keyboard focus management
 * - Responsive design with overflow management
 * - Accessibility support with ARIA labels and keyboard navigation
 * - Automatic chip updates when filter state changes
 *
 * @param props - Component props for filter management handlers
 * @returns React.FC - Functional component for filter chip display
 */
const UserManagementChips: React.FC<UserManagementChipsProps> = ({
  changeFilterAndSearch,
  clearAllFilters: clearAllFiltersProp,
}) => {
  const dispatch = useDispatch()

  // Get current filter state from Redux store
  const selectedFilters = useSelector(getUserManagementState)
  const toolFilters = selectedFilters?.toolFilters || []
  const statusFilters = selectedFilters?.statusFilters || []

  // Local state for managing chip data
  const [filterChipsData, setFilterChipsData] = useState<
    FilterChipData<UserManagementFilters>[]
  >([])

  /**
   * Update chip data whenever filter state changes
   * Converts Redux state into format expected by FilterChips component
   */
  useEffect(() => {
    getChipsData(selectedFilters).then((chipsData) => {
      setFilterChipsData(chipsData)
    })
  }, [selectedFilters, toolFilters, statusFilters])

  /**
   * Handle deletion of individual filter chips
   * Toggles the specific filter value in Redux state
   *
   * @param chipData - Data object containing filter property and value to remove
   */
  const handleDeleteChip = useCallback(
    (chipData: FilterChipData<UserManagementFilters>) => {
      const { property, value } = chipData

      // If custom handler provided, call it as well
      if (
        changeFilterAndSearch &&
        typeof changeFilterAndSearch === 'function'
      ) {
        changeFilterAndSearch(property, value)
      }
    },
    [dispatch, changeFilterAndSearch],
  )

  /**
   * Handle clearing all active filters
   * Resets filter state to initial values and manages focus
   */
  const handleClearAll = useCallback(() => {
    // Clear all filters in Redux state
    dispatch(clearAllFilters())

    // If custom clear handler provided, call it as well
    if (clearAllFiltersProp && typeof clearAllFiltersProp === 'function') {
      clearAllFiltersProp()
    }
  }, [dispatch, clearAllFiltersProp])

  // Note: Category-specific clearing handled by individual FilterChips component

  /**
   * Render FilterChips component with User Management specific configuration
   * Uses established translation key prefix and focus management
   */
  return (
    <FilterChips<UserManagementFilters>
      filtersSelected={filterChipsData}
      filterChipTranslationKeyPrefix="UserManagement"
      onChipDeleted={handleDeleteChip}
      onClearAll={handleClearAll}
      clearAllFocusQuerySelector="#user-management-filter-chips-clear-all-focus-target input"
      uniqueId="user_management_chips"
    />
  )
}

export default UserManagementChips
